import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import PropTypes from 'prop-types'
import DtoAnalysisLight from 'quality/dto/analyse/DtoAnalysisLight'
import { groupBy, keys, sumBy, round, max, orderBy, compact } from 'lodash'
import i18n from 'simple-react-i18n'
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import React, { useState, useMemo } from 'react'
import { getBeginingOfTheYear, getDate, getEndOfTheYear, getYear } from 'utils/DateUtil'
import moment from 'moment'
import { fullScreenExitIcon, fullScreenIcon, getAxisIntervalFormatter, histogramIcon, lineIcon, quantificationIcon, swapVertical } from 'components/echart/EChartUtils'
import { exportPictureIcon, exportExcelIcon } from 'components/echart/EChartUtils'
import { exportFile } from 'utils/ExportDataUtil'
import { shallowEqual, useSelector } from 'react-redux'
import { convertToRGBA, getColorCircleElement } from 'utils/ColorUtil'
import { grey, greyBlue, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import useBoolean from 'utils/customHook/useBoolean'
import { Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import NumberField from 'components/forms/NumberField'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { CATCHMENT_IRRELEVANT_PESTICIDES, CATCHMENT_PESTICIDES } from 'catchment/constants/CatchmentConstant'

const LOCAL_STORAGE_ORDINATES = 'RELEVANCE_CHART_ORDINATES'
const EXPORT_CATCHMENT_COLUMNS = ['code', 'name', 'cityCode', 'cityLabel', 'relevance', 'date', 'nbParameters', 'sum']
const EXPORT_RESOURCE_COLUMNS = ['code', 'name', 'relevance', 'date', 'nbParameters', 'sum']

const RelevanceChart = ({
    analysis = [],
    startPeriod = getBeginingOfTheYear(moment().year()),
    endPeriod = getEndOfTheYear(moment().year()),
}) => {
    const {
        cities,
        qualitometers,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const {
        value: featuresVisibility,
        setTrue: showFeatures,
        setFalse: hideFeatures,
    } = useBoolean(false)
    const {
        value: fixMinScale,
        toggle: toggleFixMinScale,
    } = useBoolean(false)
    const {
        value: bar,
        setTrue: setBar,
        setFalse: setLine,
    } = useBoolean(false)
    const {
        value: isFullScreen,
        setTrue: openFullScreen,
        setFalse: closeFullScreen,
    } = useBoolean(false)
    const {
        value: isOrdinatePopinOpen,
        toggle: toggleOrdinatePopin,
        setFalse: closeOrdinatePopin,
    } = useBoolean(false)

    const [ordinateValues, setOrdinateValues] = useLocalStorage(LOCAL_STORAGE_ORDINATES, { min: null, max: null })
    const [ordinateFields, setOrdinateFields] = useState(ordinateValues)

    const {
        formatter,
        interval,
    } = getAxisIntervalFormatter(moment(endPeriod), moment(startPeriod))

    const getRelevantPesticides = useApplicationSetting(CATCHMENT_PESTICIDES, list => list?.split(',').filter(c => !!c) || [])
    const getIrrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, list => list?.split(',').filter(c => !!c) || [])

    const periodAnalysis = useMemo(() => analysis.filter(a => a.sampleDate >= startPeriod && a.sampleDate <= endPeriod).sort((a, b) => a.sampleDate - b.sampleDate), [analysis, startPeriod, endPeriod])

    const relevantAnalysis = useMemo(() => periodAnalysis.filter(a => getRelevantPesticides.includes(a.parameter)), [periodAnalysis, getRelevantPesticides])
    const irrelevantAnalysis = useMemo(() => periodAnalysis.filter(a => getIrrelevantPesticides.includes(a.parameter)), [periodAnalysis, getIrrelevantPesticides])

    const relevantSumAnalysis = useMemo(() => {
        const groupByOperation = groupBy(relevantAnalysis, a => `${a.qualitometer}#${a.operation}`)
        return keys(groupByOperation).map(key => {
            const { [key]: randomName = [] } = groupByOperation
            return {
                result: sumBy(randomName, 'result'),
                sampleDate: randomName[0].sampleDate,
                qualitometer: randomName[0].qualitometer,
                nbParameters: groupByOperation[key].length,
            }
        })
    }, [relevantAnalysis])

    const irrelevantSumAnalysis = useMemo(() => {
        const groupByOperation = groupBy(irrelevantAnalysis, a => `${a.qualitometer}#${a.operation}`)
        return keys(groupByOperation).map(key => {
            const { [key]: randomName = [] } = groupByOperation
            return {
                result: sumBy(randomName, 'result'),
                sampleDate: randomName[0].sampleDate,
                qualitometer: randomName[0].qualitometer,
                nbParameters: groupByOperation[key].length,
            }
        })
    }, [irrelevantAnalysis])

    const relevantDates = useMemo(() => relevantSumAnalysis.map(a => a.sampleDate), [relevantSumAnalysis])
    const irrelevantDates = useMemo(() => irrelevantSumAnalysis.map(a => a.sampleDate), [irrelevantSumAnalysis])

    const missingIrrelevantDates = useMemo(() => relevantDates.filter(d => !irrelevantDates.includes(d)), [relevantDates, irrelevantDates])
    const missingRelevantDates = useMemo(() => irrelevantDates.filter(d => !relevantDates.includes(d)), [relevantDates, irrelevantDates])

    const additionnalRelevantData = useMemo(() => missingRelevantDates.map(date => ([
        parseInt(date),
        0,
        0,
        CATCHMENT_PESTICIDES,
    ])), [missingRelevantDates])

    const relevantSerie = useMemo(() => [
        ...relevantSumAnalysis.map(a => ([
            a.sampleDate,
            round(a.result, 3),
            a.nbParameters,
            CATCHMENT_PESTICIDES,
            a.qualitometer,
        ])),
        ...additionnalRelevantData,
    ].sort((a, b) => b[0] - a[0]), [relevantSumAnalysis, additionnalRelevantData])

    const additionnalIrrelevantData = useMemo(() => missingIrrelevantDates.map(date => ([
        parseInt(date),
        0,
        0,
        CATCHMENT_IRRELEVANT_PESTICIDES,
    ])), [missingIrrelevantDates])

    const irrelevantSerie = useMemo(() => [
        ...irrelevantSumAnalysis.map(a => ([
            a.sampleDate,
            round(a.result, 3),
            a.nbParameters,
            CATCHMENT_IRRELEVANT_PESTICIDES,
            a.qualitometer,
        ])),
        ...additionnalIrrelevantData,
    ].sort((a, b) => b[0] - a[0]), [irrelevantSumAnalysis, additionnalIrrelevantData])

    const resultMax = useMemo(() => max([ ...relevantSerie.map(data => data[1]), ...irrelevantSerie.map(data => data[1])]), [relevantSerie, irrelevantSerie])

    const commonDataSerie = useMemo(() => ({
        type: bar ? 'bar' : 'line',
        barWidth: 5,
        stack: 'stack',
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 1 },
    }), [bar])

    const series = useMemo(() => ([
        {
            exportName: i18n.relevantPesticides,
            name: i18n.relevantPesticides,
            data: relevantSerie,
            itemStyle: { color: convertToRGBA(PASTEL_COLOR.blue, 0.8) },
            ...commonDataSerie,
        },
        {
            exportName: i18n.irrelevantPesticides,
            name: i18n.irrelevantPesticides,
            data: irrelevantSerie,
            itemStyle: { color: convertToRGBA(PASTEL_COLOR.orange, 0.8) },
            ...commonDataSerie,
        },
    ]), [relevantSerie, irrelevantSerie, commonDataSerie])

    const startYear = useMemo(() => getYear(startPeriod), [startPeriod])
    const endYear = useMemo(() => getYear(endPeriod), [endPeriod])

    const options = {
        title: {
            text: `${i18n.relevanceSumDistribution} ${startYear === endYear ? ` (${startYear})` : ` (${startYear} - ${endYear})`}`,
            left: 'center',
            top: '5%',
        },
        series,
        xAxis: [
            {
                type: 'time',
                boundaryGap: true,
                axisLabel: {
                    rotate: 50,
                    formatter,
                },
                interval,
                min: startPeriod,
                max: endPeriod,
            },
        ],
        yAxis: [
            {
                type: 'value',
                name: `${i18n.sumPesticides} (µg/L)`,
                nameLocation: 'center',
                nameGap: 45 + max([0, round(resultMax, 0).toString().length - 4]) * 10,
                min: fixMinScale ? 0 : ordinateValues.min,
                max: ordinateValues.max,
            },
        ],
        legend: {
            top: '55',
            left: '10%',
            right: featuresVisibility ? 240 : '8%',
            type: 'scroll',
            show: true,
            data: series.map(s => ({ name: s.name, icon: 'roundRect' })),
        },
        grid: {
            top: '100',
            left: '6%',
            right: '3%',
            bottom: '10',
            containLabel: true,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
                snap: true,
            },
            formatter: params =>
                `${params?.[0]?.value?.length > 1 ? getDate(params[0].value[0]) : ''}
                ${params.reduce((acc, s) => `${acc}<br/>
                    ${getColorCircleElement(s.color)}
                    ${s.value?.[3] === CATCHMENT_PESTICIDES ? i18n.relevantPesticides : i18n.irrelevantPesticides} : 
                    ${s.value?.length > 1 ? `${s.value[1]} (µg/L)` : ''}`, '')}`,
        },
        toolbox: {
            top: '50',
            right: '35',
            showTitle: false,
            itemSize: '18',
            tooltip: {
                show: true,
                position: 'bottom',
            },
            feature: {
                myToolLine: {
                    show: featuresVisibility,
                    title: i18n.lines,
                    icon: lineIcon,
                    onclick: () => setLine(),
                    iconStyle: {
                        borderColor: !bar ? greyBlue : grey,
                    },
                },
                myToolBar: {
                    show: featuresVisibility,
                    title: i18n.histogram,
                    icon: histogramIcon,
                    onclick: () => setBar(),
                    iconStyle: {
                        borderColor: bar ? greyBlue : grey,
                    },
                },
                myToolFullScreen: {
                    show: featuresVisibility,
                    title: i18n.fullScreen,
                    icon: isFullScreen ? fullScreenExitIcon : fullScreenIcon,
                    onclick: () => isFullScreen ? closeFullScreen() : openFullScreen(),
                },
                myToolQuantification: {
                    show: featuresVisibility,
                    title: i18n.displayYAxis0,
                    icon: quantificationIcon,
                    onclick: () => toggleFixMinScale(),
                    iconStyle: {
                        borderColor: fixMinScale ? greyBlue : grey,
                    },
                },
                myZoom: {
                    show: featuresVisibility,
                    title: i18n.zoomVertical,
                    icon: swapVertical,
                    onclick: () => toggleOrdinatePopin(),
                    iconStyle: {
                        borderColor: isOrdinatePopinOpen ? greyBlue : grey,
                    },
                },
                saveAsImage: {
                    show: featuresVisibility,
                    title: i18n.pictureExport,
                    icon: exportPictureIcon,
                    name: i18n.relevanceSumDistribution,
                },
                myToolExport: {
                    show: featuresVisibility,
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: () => {
                        const exportData = series.flatMap(s => compact(s.data?.map(a => {
                            const analysisQualito = a[4]
                            const qualitometer = qualitometers.find(q => analysisQualito === q.id)
                            const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
                            const citiesColumns = cities?.length > 0 ?
                                {
                                    cityCode: city?.code,
                                    cityLabel: city?.name,
                                } : {}
                            return a[2] > 0 ? {
                                code: { value: qualitometer?.code, cellType: 'right' },
                                name: qualitometer?.name,
                                ...citiesColumns,
                                relevance: s.exportName,
                                timestamp: a[0],
                                date: getDate(parseInt(a[0])),
                                nbParameters: a[2],
                                sum: { value: a[1], format: '0.00' },
                            } : undefined
                        })))
                        const sortedData = orderBy(exportData, ['relevance', 'timestamp'], ['desc', 'desc'])
                        exportFile({
                            data: exportData.length ? [
                                {
                                    ...sortedData[0],
                                    headers: cities?.length > 0 ? EXPORT_CATCHMENT_COLUMNS : EXPORT_RESOURCE_COLUMNS,
                                },
                                ...sortedData.slice(1),
                            ] : [],
                            exportType: 'xlsx',
                            titleFile: i18n.relevanceSumDistribution,
                        }, true)
                    },
                },
            },
        },
    }

    return (
        <>
            <div
                onMouseOver={() => showFeatures()}
                onMouseOut={() => hideFeatures()}
            >
                <ReactECharts
                    echarts={echarts}
                    option={options}
                    notMerge
                    lazyUpdate
                    style={{ height: 400 }}
                />
            </div>
            <Dialog
                fullScreen
                open={isFullScreen}
            >
                <DialogContent>
                    <div
                        onMouseOver={() => showFeatures()}
                        onMouseOut={() => hideFeatures()}
                    >
                        <ReactECharts
                            echarts={echarts}
                            option={options}
                            notMerge
                            lazyUpdate
                            style={{ height: '75vh' }}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFullScreen} variant='contained'>
                        {i18n.close}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={isOrdinatePopinOpen}
            >
                <DefaultDialogTitle
                    title={i18n.zoomVertical}
                    onClose={() => closeOrdinatePopin()}
                />
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid size={6}>
                            <NumberField
                                title={i18n.min}
                                value={ordinateFields.min}
                                onChange={v => setOrdinateFields(prev => ({ ...prev, min: v }))}
                                floatValue
                                onEnterKeyPress={v => {
                                    setOrdinateFields(prev => ({ ...prev, min: v }))
                                    setOrdinateValues({ min: v, max: ordinateFields.max })
                                    closeOrdinatePopin()
                                }}
                            />
                        </Grid>
                        <Grid size={6}>
                            <NumberField
                                title={i18n.max}
                                value={ordinateFields.max}
                                onChange={v => setOrdinateFields(prev => ({ ...prev, max: v }))}
                                floatValue
                                onEnterKeyPress={v => {
                                    setOrdinateFields(prev => ({ ...prev, max: v }))
                                    setOrdinateValues({ min: ordinateFields.min, max: v })
                                    closeOrdinatePopin()
                                }}
                            />
                        </Grid>
                        <Grid size={3} offset={6}>
                            <Button
                                onClick={() => setOrdinateFields({ min: null, max: null })}
                                variant='outlined'
                                fullWidth
                            >
                                {i18n.clean}
                            </Button>
                        </Grid>
                        <Grid size={3}>
                            <Button
                                onClick={() => {
                                    setOrdinateValues({ min: ordinateFields.min, max: ordinateFields.max })
                                    closeOrdinatePopin()
                                }}
                                variant='contained'
                                fullWidth
                            >
                                {i18n.apply}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    )
}

RelevanceChart.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
}

export default RelevanceChart